import type { FC } from 'react';
import { Content, Header, Page } from '@backstage/core-components';
import {
  DevHubBox,
  DevHubButton,
  DevHubTypography,
  DevHubCircularProgress,
  DevHubPaper,
  DevHubDynamicIcon,
  DevHubTextField,
} from '@devxdiscover/devhub-ui';

// CSS animations for slide transitions
const slideAnimationStyles = `
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

// CSS for left-aligned text field with improved contrast
const leftAlignedTextFieldStyles = `
  .left-aligned-textfield {
    margin: 0 !important;
  }
  
  /* Left alignment for input text */
  .left-aligned-textfield input,
  .left-aligned-textfield .MuiInputBase-input {
    text-align: left !important;
    font-size: 0.875rem !important;
    color: #333333 !important;
  }
  
  /* Placeholder text contrast */
  .left-aligned-textfield input::placeholder {
    color: #555555 !important;
  }
  
  /* Remove margins from form controls */
  .left-aligned-textfield .MuiFormControl-root,
  .left-aligned-textfield .MuiTextField-root {
    margin: 0 !important;
  }
`;

// Inject styles into document head
if (
  typeof document !== 'undefined' &&
  !document.getElementById('data-lead-animations')
) {
  const styleElement = document.createElement('style');
  styleElement.id = 'data-lead-animations';
  styleElement.textContent = slideAnimationStyles + leftAlignedTextFieldStyles;
  document.head.appendChild(styleElement);
}
import DevHubSupportButton from '../../../common/DevHubSupportButton';
import {
  ApplicationsTable,
  PageHeaderSection,
  SelectAllDialog,
  SwitchConfirmDialog,
  CancelConfirmDialog,
  DataLeadChangesReviewDialog,
  DataLeadSubmissionReview,
  ChangesSummary,
} from './index';
import {
  FunctionalArea,
  DataLeadWithFullName,
  Application,
  AssignmentMode,
} from '../types';

export interface ManageDataLeadsPresenterProps {
  // Functional Area Data
  functionalAreas: FunctionalArea[];
  selectedFunctionalArea: string;
  activeFunctionalAreaApps: Application[];
  completeFunctionalAreasData: FunctionalArea[];
  functionalAreaDataLeads: DataLeadWithFullName[];

  // Admin Functionality
  isAdmin?: boolean;
  showAllFunctionalAreas?: boolean;
  onAdminToggle?: (checked: boolean) => void;

  // Loading States
  isRefreshing: boolean;
  isFetchingApplicationsForArea: boolean;
  isLoadingFunctionalAreas?: boolean;

  // Table State
  matrixTableRows: any[];
  globalFilterValue: string;
  currentPageSize: number;
  selectedAppsForUpdate: Set<number>;

  // Dialog States
  showSelectAllDialog: boolean;
  selectAllDialogData: {
    leadLogin: string;
    targetValue: boolean;
    visibleRows: any[];
    currentPageCount: number;
  } | null;
  showSwitchConfirmDialog: boolean;
  showCancelConfirmDialog: boolean;
  showChangesReviewDialog: boolean;
  showSubmissionReview: boolean;
  submitChangesViewMode: 'per-app' | 'per-lead';

  // Data Manager
  dataLeadManager: {
    hasRowChanges: (applicationId: number) => boolean;
    applicationModes: Map<number, AssignmentMode>;
    [key: string]: any;
  };

  // Event Handlers
  onFunctionalAreaChange: (area: string) => void;
  onFilterChange: (value: string) => void;
  onRefresh: () => void;
  onMatrixPageChange: (event: any) => void;
  onMatrixSelectionChange: (value: any) => void;
  onExecuteSelectAll: (
    leadLogin: string,
    rows: any[],
    targetValue: boolean,
  ) => void;
  onStayOnPage: () => void;
  onConfirmExit: () => void;
  onConfirmSwitch: () => void;
  onCancelSwitch: () => void;
  onCloseSelectAllDialog: () => void;
  onCancel: () => void;
  onResetToOriginal: () => void;
  onSubmit: () => void;
  onCloseChangesReviewDialog: () => void;
  onConfirmChangesReview: () => void;
  onViewModeChange: (mode: 'per-app' | 'per-lead') => void;
  onCloseSubmissionReview: () => void;
  onConfirmSubmissionReview: (successfulApplicationIds: number[]) => void;
  onProcessingComplete: (successfulApplicationIds: number[]) => void;

  // Additional Data
  templateProps: any;
}

/**
 * Pure presenter component for Data Lead Management
 * This component only handles rendering and delegates all business logic to the container
 * @param props - The component props containing all required data and callbacks
 * @returns The rendered ManageDataLeadsPresenter component
 */
export const ManageDataLeadsPresenter: FC<ManageDataLeadsPresenterProps> = ({
  // Functional Area Data
  functionalAreas,
  selectedFunctionalArea,
  activeFunctionalAreaApps,
  completeFunctionalAreasData,
  functionalAreaDataLeads,

  // Admin Functionality
  isAdmin = false,
  showAllFunctionalAreas = false,
  onAdminToggle,

  // Loading States
  isRefreshing,
  isFetchingApplicationsForArea,
  isLoadingFunctionalAreas = false,

  // Table State
  matrixTableRows,
  globalFilterValue,
  currentPageSize,
  selectedAppsForUpdate,

  // Dialog States
  showSelectAllDialog,
  selectAllDialogData,
  showSwitchConfirmDialog,
  showCancelConfirmDialog,
  showChangesReviewDialog,
  showSubmissionReview,
  submitChangesViewMode,

  // Data Manager
  dataLeadManager,

  // Event Handlers
  onFunctionalAreaChange,
  onFilterChange,
  onRefresh,
  onMatrixPageChange,
  onMatrixSelectionChange,
  onExecuteSelectAll,
  onStayOnPage,
  onConfirmExit,
  onConfirmSwitch,
  onCancelSwitch,
  onCloseSelectAllDialog,
  onCancel,
  onResetToOriginal,
  onSubmit,
  onCloseChangesReviewDialog,
  onConfirmChangesReview,
  onViewModeChange,
  onCloseSubmissionReview,
  onConfirmSubmissionReview,
  onProcessingComplete,

  // Additional Data
  templateProps,
}) => {
  // Create table header component
  const tableHeaderComponent = () => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <DevHubTextField
          type="text"
          placeholder="Filter applications..."
          value={globalFilterValue}
          onChange={(e: any) => onFilterChange(e.target.value)}
          variant="outlined"
          size="small"
          className="left-aligned-textfield"
          InputProps={{
            startAdornment: (
              <DevHubDynamicIcon
                iconName="FilterList"
                style={{
                  marginRight: '0.5rem',
                  color: '#666',
                  fontSize: '0.875rem',
                }}
              />
            ),
            style: {
              fontSize: '0.875rem',
            },
          }}
          inputProps={{
            'aria-label':
              'Filter applications by name, owner, or functional area',
            style: {
              fontSize: '0.875rem',
            },
          }}
          style={{
            minWidth: '300px',
            maxWidth: '300px',
            margin: 0,
            fontSize: '0.875rem',
          }}
        />
      </div>
      <DevHubBox display="flex" alignItems="center" style={{ gap: '0.5rem' }}>
        <DevHubButton
          variant="outlined"
          color="primary"
          size="large"
          onClick={onRefresh}
          disabled={isRefreshing}
          startIcon={
            isRefreshing ? (
              <DevHubCircularProgress size={16} />
            ) : (
              <DevHubDynamicIcon iconName="Sync" />
            )
          }
        >
          {isRefreshing ? 'Refreshing...' : 'Refresh'}
        </DevHubButton>
      </DevHubBox>
    </div>
  );

  return (
    <Page themeId="home">
      <Header
        title="Data Leads"
        subtitle="Manage Data Lead assignments for applications"
      >
        <DevHubBox display="flex" justifyContent="flex-end">
          <DevHubSupportButton color="white" />
        </DevHubBox>
      </Header>

      <Content>
        <DevHubPaper>
          <DevHubBox padding="large">
            {/* Unified Status Banner - Handles both initial state and initial loading with smooth transitions */}
            {(!selectedFunctionalArea || isFetchingApplicationsForArea) && (
              <DevHubBox
                style={{
                  marginBottom: '2rem',
                  padding: '1.25rem 1.5rem',
                  backgroundColor: '#e3f2fd',
                  color: '#1565c0',
                  borderRadius: '8px',
                  border: '1px solid #bbdefb',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  boxShadow: '0 2px 4px rgba(21, 101, 192, 0.1)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  minHeight: '3.5rem',
                  opacity:
                    !selectedFunctionalArea || isFetchingApplicationsForArea
                      ? 1
                      : 0,
                  transform:
                    !selectedFunctionalArea || isFetchingApplicationsForArea
                      ? 'translateY(0)'
                      : 'translateY(-10px)',
                }}
              >
                {/* Animated loading spinner - only shows during initial fetch */}
                {isFetchingApplicationsForArea && (
                  <DevHubCircularProgress
                    size={20}
                    style={{
                      opacity: isFetchingApplicationsForArea ? 1 : 0,
                      transition: 'opacity 0.2s ease-out',
                    }}
                  />
                )}

                {/* Dynamic content based on state */}
                <DevHubTypography
                  variant="subtitle1"
                  style={{
                    color: '#1565c0',
                    fontWeight: 500,
                    margin: 0,
                    transition: 'all 0.2s ease-out',
                    flex: 1,
                  }}
                >
                  {!selectedFunctionalArea &&
                    '📋 Get started by selecting a functional area to manage data lead assignments'}
                  {selectedFunctionalArea && isFetchingApplicationsForArea && (
                    <>
                      Loading applications for{' '}
                      <strong>{selectedFunctionalArea}</strong>...
                    </>
                  )}
                </DevHubTypography>
              </DevHubBox>
            )}

            <PageHeaderSection
              selectedFunctionalArea={selectedFunctionalArea}
              onFunctionalAreaChange={onFunctionalAreaChange}
              functionalAreas={functionalAreas.map(fa => fa.name)}
              isLoadingFunctionalAreas={isLoadingFunctionalAreas}
              isAdmin={isAdmin}
              showAllFunctionalAreas={showAllFunctionalAreas}
              onAdminToggle={onAdminToggle}
            />

            {/* Applications Content Area - Smooth transition from loading to table */}
            {selectedFunctionalArea && (
              <DevHubBox
                style={{
                  opacity: matrixTableRows.length > 0 ? 1 : 0,
                  transform:
                    matrixTableRows.length > 0
                      ? 'translateY(0)'
                      : 'translateY(20px)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  marginTop: '1rem',
                }}
              >
                {matrixTableRows.length > 0 && (
                  <ApplicationsTable
                    matrixTableRows={matrixTableRows}
                    functionalAreaDataLeads={functionalAreaDataLeads}
                    selectedFunctionalArea={selectedFunctionalArea}
                    activeFunctionalAreaApps={activeFunctionalAreaApps}
                    globalFilterValue={globalFilterValue}
                    currentPageSize={currentPageSize}
                    selectedAppsForUpdate={selectedAppsForUpdate}
                    isRefreshing={isRefreshing}
                    onPage={onMatrixPageChange}
                    onSelectionChange={onMatrixSelectionChange}
                    templateProps={templateProps}
                    dataLeadManager={dataLeadManager}
                    header={tableHeaderComponent}
                  />
                )}
              </DevHubBox>
            )}

            {/* Empty State */}
            {selectedFunctionalArea &&
              !isRefreshing &&
              !isFetchingApplicationsForArea &&
              matrixTableRows.length === 0 && (
                <DevHubBox
                  style={{
                    marginTop: '2rem',
                    padding: '2rem',
                    textAlign: 'center',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e9ecef',
                  }}
                >
                  <h3>No applications found</h3>
                  <p>
                    There are no applications in the selected functional area:{' '}
                    {selectedFunctionalArea}
                  </p>
                </DevHubBox>
              )}

            {/* Main Content Area - Only show when functional area is selected */}

            {/* Dialogs */}
            <SelectAllDialog
              open={showSelectAllDialog}
              onClose={onCloseSelectAllDialog}
              selectAllDialogData={selectAllDialogData}
              activeFunctionalAreaApps={activeFunctionalAreaApps}
              onExecuteSelectAll={onExecuteSelectAll}
            />

            <SwitchConfirmDialog
              open={showSwitchConfirmDialog}
              onCancel={onCancelSwitch}
              onConfirm={onConfirmSwitch}
            />

            <CancelConfirmDialog
              open={showCancelConfirmDialog}
              onStay={onStayOnPage}
              onConfirmExit={onConfirmExit}
            />

            <DataLeadChangesReviewDialog
              open={showChangesReviewDialog}
              onClose={onCloseChangesReviewDialog}
              onConfirm={onConfirmChangesReview}
              changes={dataLeadManager.getChangesSummary(selectedAppsForUpdate)}
              viewMode={submitChangesViewMode}
              onViewModeChange={onViewModeChange}
            />

            <DataLeadSubmissionReview
              open={showSubmissionReview}
              onClose={onCloseSubmissionReview}
              onConfirm={onConfirmSubmissionReview}
              changes={dataLeadManager.getChangesSummary(selectedAppsForUpdate)}
              applications={activeFunctionalAreaApps}
              functionalAreasData={completeFunctionalAreasData}
              selectedFunctionalArea={selectedFunctionalArea}
              currentAssignments={
                dataLeadManager.dataLeadAssignments || new Map()
              }
              onProcessingComplete={onProcessingComplete}
            />

            {/* Selection Summary */}
            {selectedFunctionalArea && activeFunctionalAreaApps.length > 0 && (
              <DevHubBox
                style={{
                  marginTop: '1rem',
                  padding: '0.75rem',
                  backgroundColor: '#f0f7ff',
                  borderRadius: '4px',
                  border: '1px solid #cce7ff',
                  opacity: matrixTableRows.length > 0 ? 1 : 0,
                  transform:
                    matrixTableRows.length > 0
                      ? 'translateY(0)'
                      : 'translateY(15px)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1) 0.2s',
                }}
              >
                <DevHubTypography variant="body2" style={{ color: '#1565c0' }}>
                  📋 <strong>Selection:</strong> {selectedAppsForUpdate.size} of{' '}
                  {activeFunctionalAreaApps.length} applications selected for
                  updates
                  {selectedAppsForUpdate.size <
                    activeFunctionalAreaApps.length && (
                    <span
                      style={{
                        marginLeft: '1rem',
                        fontSize: '0.85em',
                        color: '#757575',
                      }}
                    >
                      (
                      {activeFunctionalAreaApps.length -
                        selectedAppsForUpdate.size}{' '}
                      will be ignored)
                    </span>
                  )}
                </DevHubTypography>
              </DevHubBox>
            )}

            {/* Changes Summary */}
            {selectedFunctionalArea &&
              activeFunctionalAreaApps.length > 0 &&
              selectedAppsForUpdate.size > 0 && (
                <DevHubBox
                  style={{
                    opacity: matrixTableRows.length > 0 ? 1 : 0,
                    transform:
                      matrixTableRows.length > 0
                        ? 'translateY(0)'
                        : 'translateY(15px)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1) 0.3s',
                  }}
                >
                  <ChangesSummary
                    changes={dataLeadManager.getChangesSummary(
                      selectedAppsForUpdate,
                    )}
                  />
                </DevHubBox>
              )}

            {/* Action Buttons */}
            {selectedFunctionalArea && activeFunctionalAreaApps.length > 0 && (
              <DevHubBox
                style={{
                  marginTop: '2rem',
                  display: 'flex',
                  gap: '1rem',
                  justifyContent: 'flex-end',
                  opacity: matrixTableRows.length > 0 ? 1 : 0,
                  transform:
                    matrixTableRows.length > 0
                      ? 'translateY(0)'
                      : 'translateY(15px)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1) 0.4s',
                }}
              >
                <DevHubButton
                  variant="outlined"
                  color="primary"
                  size="large"
                  onClick={onCancel}
                >
                  Back to Applications
                </DevHubButton>

                <DevHubButton
                  variant="outlined"
                  color="primary"
                  size="large"
                  disabled={
                    !activeFunctionalAreaApps.some(app =>
                      dataLeadManager.hasRowChanges(app.applicationId),
                    )
                  }
                  onClick={onResetToOriginal}
                >
                  Reset to Original
                </DevHubButton>

                <DevHubButton
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={onSubmit}
                  disabled={
                    (activeFunctionalAreaApps.length > 1 &&
                      selectedAppsForUpdate.size === 0) ||
                    dataLeadManager.getChangesSummary(selectedAppsForUpdate)
                      .length === 0
                  }
                >
                  Review Changes
                </DevHubButton>
              </DevHubBox>
            )}
          </DevHubBox>
        </DevHubPaper>
      </Content>
    </Page>
  );
};
